/* eslint-disable i18next/no-literal-string -- TanStack cache key segments */
/**
 * `entities/project-folder` TanStack Query key factory.
 *
 * Tuple shapes are INLINED to match the historical `PROJECTS_QUERY_KEYS.folders.*`
 * factory in `entities/project/api/query-keys.ts` byte-for-byte. Two reasons:
 *
 *   1. Cache compatibility: existing TanStack caches populated under the old
 *      tuples MUST keep hitting after the relocation. The cache-contract test
 *      pins the shape.
 *   2. FSD boundary: the cross-entity-value-only ESLint rule
 *      (`crossEntityTypeOnlyConfig` in eslint.config.js) blocks importing
 *      `PROJECTS_QUERY_KEYS` from `@/entities/project` by value inside any
 *      `src/entities/**` file. Inlining the tuples removes the dependency.
 *
 * Drift prevention: the cache-contract test uses inline snapshots that match
 * the snapshots in `entities/project/api/__tests__/cache-contract.test.ts`
 * (the `folders.*` block). If either side changes shape, both snapshots
 * fail loudly in CI.
 */

export const PROJECT_FOLDER_QUERY_KEYS = {
  /**
   * Every folder key for a project — lists, details and file sets alike.
   * A write that lands on a folder row from outside this slice (linking a
   * brief, for one) invalidates the lot rather than guessing which folder
   * ids a given screen is holding.
   */
  allForProject: (projectId: string) => ['project', projectId, 'folders'] as const,

  /**
   * Paginated/searched/sorted folder list under a project. `parentId` defaults
   * to the literal `'root'` so the top-level folder list always has a stable
   * key (matches the historical PROJECTS_QUERY_KEYS.folders.list shape).
   */
  list: (projectId: string, parentId?: string) =>
    ['project', projectId, 'folders', 'list', parentId ?? 'root'] as const,

  /** Single folder by id. */
  detail: (projectId: string, folderId: string) =>
    ['project', projectId, 'folders', 'detail', folderId] as const,

  /**
   * Files inside a specific folder. Note the `folderId` slot lands BEFORE
   * the `'files'` tag — documented quirk preserved from the legacy factory
   * so existing caches remain valid.
   */
  files: (projectId: string, folderId: string) =>
    ['project', projectId, 'folders', folderId, 'files'] as const,
} as const;
